import React, { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import commonSelector from '@store/common/selectors';
import featureNames from '@utils/featureToggle/constants';
import { sharingSelector } from '@store/timeline/sharing/selectors';
import {
  SaveDashboardAdapter,
  DeleteDashboardAdapter,
} from '@components/common/Dashboard/FunctionalButton';
import { getViewerModuleTempDashboardUrl } from '@components/common/Dashboard/utils/getTempDashboardUrl';
import { Export } from '@components/common';
import { SharingAdapter } from '@components/common/Sharing';
import {
  sessionIDSelector,
  viewerIdSelector,
  isFetchingSelector,
} from '@store/timeline/data/selectors';
import { DASHBOARD_TYPE, PAGE_TYPE, EXPORT_TIMELINE } from '@utils/constants';
import { exportData } from '@store/export/actions';
import { createTempDashboard } from '@store/tempDashboard/actions';
import { Button } from 'antd';
import { StyledToolbox, StyleTitle, StyledMoreDropDown } from './styled';
import { StyledAlert } from '@components/dimension-panel-hz/dimension-table/styled';
import { InfoCircleOutlined } from '@ant-design/icons';
import { get } from 'lodash';

interface IProps {
  id: number | string;
  pageType: number;
  dashboard: any;
}

const { baseUrl } = window.conviva;

const Toolbox = ({ id, pageType, dashboard }: IProps) => {
  const sessionId = useSelector(sessionIDSelector);
  const viewerId = useSelector(viewerIdSelector);
  const isFetching = useSelector(isFetchingSelector);
  const { features } = useSelector(commonSelector);
  const share = features[featureNames.SHARE];
  const save = features[featureNames.SAVED_DASHBOARDS];
  const showExport = features[featureNames.EXPORT_DATA];
  const sharingState = useSelector(sharingSelector);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onExport = useCallback(
    (context) => {
      dispatch(exportData(context));
    },
    [dispatch],
  );

  const displayVID = viewerId || 'NA';
  const isClickableVID = displayVID !== 'NA';

  const handleSearch: any = () => {
    if (displayVID !== 'NA') {
      // callback to push new temp tab url
      const redirectHandler = ({ id }) => {
        navigate(getViewerModuleTempDashboardUrl(displayVID, id));
      };
      // create local storage entry
      dispatch(
        createTempDashboard(
          DASHBOARD_TYPE.VIEWER_MODULE,
          { viewerId: displayVID },
          `Viewer - ${displayVID}`,
          { redirectHandler },
        ),
      );
    }
  };

  const ClickableVID = () => (
    <StyleTitle>
      Viewer ID:
      <Button
        onClick={handleSearch}
        type="link"
        style={{
          fontSize: '24px',
        }}
      >
        {displayVID}
      </Button>
    </StyleTitle>
  );

  if (isFetching || !sessionId) {
    // dashboard request is initialized as an object in reducer, hence it will be always defined
    if (dashboard.request.meta?.isLiveSession) {
      return (
        <StyledToolbox>
          Loading...
          <StyledAlert
            message="Viewer and Session Timeline data for Live sessions may be delayed by 7-15 minutes."
            type="info"
            showIcon
            icon={<InfoCircleOutlined style={{ color: '#3392ED' }} />}
          />
        </StyledToolbox>
      );
    }

    return <StyledToolbox>Loading...</StyledToolbox>;
  }

  const getDropdownItems = () => {
    const items: Array<{ key: string; label: React.ReactNode }> = [];
    if (share) {
      items.push({
        key: 'share',
        label: (
          <SharingAdapter url={`${baseUrl}/dashboard/timeline/sharing`} config={sharingState} />
        ),
      });
    }
    if (save) {
      items.push({
        key: 'save',
        label: (
          <SaveDashboardAdapter
            id={id}
            pageType={pageType}
            dashboard={dashboard}
            dashboardType={DASHBOARD_TYPE.TIMELINE}
          />
        ),
      });
    }
    if (showExport) {
      items.push({
        key: 'export',
        label: <Export context={EXPORT_TIMELINE} clickCallback={onExport} showIcon={false} />,
      });
    }
    if (PAGE_TYPE.SAVED_DASHBOARD === pageType) {
      items.push({
        key: 'delete',
        label: <DeleteDashboardAdapter id={id} pageType={pageType} dashboard={dashboard} />,
      });
    }
    return items;
  };

  return (
    <StyledToolbox>
      <StyleTitle>{`Session ID: ${sessionId || 'N/A'}`}</StyleTitle>
      {isClickableVID ? <ClickableVID /> : <StyleTitle>Viewer ID: N/A</StyleTitle>}
      <StyledMoreDropDown menu={{ items: getDropdownItems() }} />
    </StyledToolbox>
  );
};

export default Toolbox;
